import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-document-view-modal',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  templateUrl: './document-view-modal.component.html',
  styleUrl: './document-view-modal.component.scss'
})
export class DocumentViewModalComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);

  @Input() title = this.transloco.translate('compliance.shared.modals.documentView.title');
  @Input() documentName = '';
  @Input() documentUrl: string | null = null;

  @Input() downloadHandler?: () => Promise<string | null>;

  hasPreviewError = false;
  isImageLoading = true;
  isDownloading = false;

  previewUrl: string | null = null;
  private objectUrl: string | null = null;

  public activeModal = inject(NgbActiveModal);

  get hasDocument(): boolean {
    return !!this.documentUrl;
  }

  ngOnInit(): void {
    void this.preloadImage();
  }

  ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  private async preloadImage(): Promise<void> {
    if (!this.documentUrl) {
      this.isImageLoading = false;
      return;
    }

    try {
      const response = await fetch(this.documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }
      const blob = await response.blob();
      this.objectUrl = URL.createObjectURL(blob);

      const image = new Image();
      image.src = this.objectUrl;
      await image.decode();

      this.previewUrl = this.objectUrl;
    } catch {
      try {
        const image = new Image();
        image.src = this.documentUrl;
        await image.decode();
        this.previewUrl = this.documentUrl;
      } catch {
        this.hasPreviewError = true;
      }
    } finally {
      this.isImageLoading = false;
    }
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  async onDownload(): Promise<void> {
    if (this.isDownloading) return;

    let url = this.documentUrl;
    if (this.downloadHandler) {
      this.isDownloading = true;
      try {
        url = await this.downloadHandler();
      } finally {
        this.isDownloading = false;
      }
    }

    if (!url) return;

    const a = document.createElement('a');
    a.href = url;
    a.download = this.documentName || 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
